import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { BadgeCheck, Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Field } from "@/components/forms";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSkills } from "@/hooks/useReferenceData";
import { useCreateSlot } from "@/hooks/usePartnerData";

interface Skill {
  id: number;
  name: string;
}

export const Route = createFileRoute("/partner/slots/new")({ component: CreateSlot });

function CreateSlot() {
  const [done, setDone] = useState(false);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [duration, setDuration] = useState("");
  const [positions, setPositions] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const createSlot = useCreateSlot();
  const skillsResult = useSkills();
  const skillsData = skillsResult.data as (string | Skill)[] | undefined;
  const skillNames = (skillsData || []).map((s) => (typeof s === "string" ? s : s.name));

  const handleCreate = async () => {
    if (title && location && positions) {
      try {
        await createSlot.mutateAsync({
          title,
          description,
          requirements: selectedSkills.join(", "),
          location,
          duration: duration || "Flexible",
          max_students: Number(positions),
        });
        setDone(true);
      } catch (err) {
        console.error("Slot creation failed:", err);
      }
    }
  };

  if (done) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Slot published"
          subtitle="Your placement opportunity is now active and visible to eligible student candidates."
          breadcrumbs={[{ label: "Slots", path: "/partner/slots" }, { label: "New" }]}
        />
        <div className="mx-auto max-w-2xl">
          <SectionCard title="Success configuration">
            <div className="flex flex-col items-center py-4 text-center">
              <div className="mb-4 flex size-10 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
                <BadgeCheck className="size-5" aria-hidden />
              </div>
              <h2 className="text-title text-foreground">Placement slot active</h2>
              <p className="mt-1 max-w-md text-sm leading-6 text-muted-foreground">
                The <span className="font-medium text-foreground">{title}</span> role has been
                successfully listed in the placement directory.
              </p>

              <dl className="mt-6 w-full space-y-4 rounded-md border border-border bg-muted/40 p-4 text-left">
                <div>
                  <dt className="eyebrow mb-1.5">Role details</dt>
                  <dd>
                    <p className="text-base font-medium text-foreground">{title}</p>
                    <p className="mt-0.5 text-caption text-muted-foreground">
                      {location} • {positions} Positions Available
                    </p>
                  </dd>
                </div>
                {selectedSkills.length > 0 && (
                  <div>
                    <dt className="eyebrow mb-1.5">Required proficiency</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {selectedSkills.map((skill) => (
                        <Badge key={skill} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="mt-6 flex w-full flex-col-reverse justify-center gap-2 sm:flex-row">
                <Button
                  onClick={() => {
                    setDone(false);
                    setTitle("");
                    setLocation("");
                    setPositions("");
                    setSelectedSkills([]);
                    setDescription("");
                  }}
                  variant="outline"
                >
                  Create another
                </Button>
                <Button asChild>
                  <Link to="/partner/slots">View all slots</Link>
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create placement slot"
        subtitle="Define a new industrial placement opportunity and target specific student skillsets."
        breadcrumbs={[{ label: "Slots", path: "/partner/slots" }, { label: "New" }]}
      />

      <div className="mx-auto max-w-2xl">
        <SectionCard
          title="Placement blueprint"
          description="Specify the operational and technical requirements for this role."
        >
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-[7fr_5fr]">
              <Field label="Role title" required>
                <Input
                  placeholder="e.g. Graduate Engineering Trainee"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label="Positions" required>
                <Input
                  type="number"
                  placeholder="e.g. 5"
                  value={positions}
                  onChange={(e) => setPositions(e.target.value)}
                  min={1}
                />
              </Field>
            </div>

            <Field label="Location" required>
              <Input
                placeholder="e.g. Dar es Salaam or Remote"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </Field>

            <Field label="Duration">
              <Input
                placeholder="e.g. 8 weeks or 3 months"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </Field>

            <Field label="Technical prerequisites">
              {({ id, describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  options={skillNames}
                  value={selectedSkills.filter((s) => skillNames.includes(s))}
                  onChange={setSelectedSkills}
                  placeholder="Select required technical skills"
                  searchPlaceholder="Search skills…"
                />
              )}
            </Field>

            <Field label="Role description & scope">
              <Textarea
                placeholder="Provide a comprehensive overview of the role, responsibilities, and learning outcomes..."
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button variant="outline" asChild>
                <Link to="/partner/slots">Cancel</Link>
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!title || !location || !positions}
                loading={createSlot.isPending}
              >
                {!createSlot.isPending && <Plus />}
                {createSlot.isPending ? "Publishing..." : "Publish placement slot"}
              </Button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
